
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

const PaymentSuccess = () => {
  const [status, setStatus] = useState<"success" | "error" | "loading">("loading");
  const [message, setMessage] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  useEffect(() => {
    const processPayment = async () => {
      try {
        // Récupérer les paramètres de l'URL
        const params = new URLSearchParams(location.search);
        const paymentStatus = params.get("payment_status");
        const sessionId = params.get("session_id");
        
        if (paymentStatus === "cancelled") {
          setStatus("error");
          setMessage("Le paiement a été annulé. Vous pouvez réessayer ultérieurement.");
          return;
        }
        
        if (paymentStatus === "success" && sessionId) {
          // Vérifier le statut de la session auprès de l'API Stripe via notre fonction
          const { data, error } = await supabase.functions.invoke('verify-payment', {
            body: { sessionId },
          });
          
          if (error) {
            throw error;
          }
          
          if (data?.status === "complete") {
            setStatus("success");
            setMessage("Paiement réussi ! Votre abonnement est maintenant actif.");
            
            toast({
              title: "Paiement réussi",
              description: "Votre abonnement a été activé avec succès.",
              variant: "default",
            });
          } else {
            throw new Error("Le statut de la session n'est pas complet");
          }
        } else {
          throw new Error("Paramètres invalides");
        }
      } catch (error) {
        console.error("Erreur lors du traitement du paiement:", error);
        setStatus("error");
        setMessage("Une erreur est survenue lors du traitement de votre paiement. Veuillez contacter notre support.");
        
        toast({
          title: "Erreur de paiement",
          description: "Un problème est survenu lors de la validation de votre paiement.",
          variant: "destructive",
        });
      }
    };
    
    processPayment();
  }, [location, toast]);
  
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-pastel-blue-light px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="text-center">
          {status === "loading" ? (
            <div className="flex justify-center">
              <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-socialsync-600"></div>
            </div>
          ) : status === "success" ? (
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
          ) : (
            <XCircle className="mx-auto h-16 w-16 text-red-500" />
          )}
          
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            {status === "loading" 
              ? "Traitement du paiement..." 
              : status === "success" 
                ? "Paiement réussi !" 
                : "Paiement échoué"}
          </h2>
          
          <p className="mt-2 text-gray-600">
            {message || (status === "loading" ? "Veuillez patienter pendant que nous vérifions votre paiement..." : "")}
          </p>
        </div>
        
        <div className="mt-8 flex justify-center">
          <Button
            onClick={() => navigate("/dashboard")}
            className="w-full"
          >
            Retourner au tableau de bord
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
